import type { ErrorObject, ValidateFunction, SchemaObject } from 'ajv';

type SchemaProperty = {
  type: string;
};

export enum DefaultValueTypes {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
}

/**
 * Gets the default value for a given type
 * @param type of data for which default value is being generated
 * @returns default value for the given type
 */
export function getDefaultValueForType(
  type: string,
): string | number | boolean {
  switch (type) {
    case DefaultValueTypes.String: {
      return '';
    }
    case DefaultValueTypes.Number: {
      return 0;
    }
    case DefaultValueTypes.Boolean: {
      return false;
    }
    default: {
      throw new Error(`unknown data type: ${type}`);
    }
  }
}

/**
 * @param schema - The JSON schema for the object.
 * @returns A JavaScript object with all required properties set to default values.
 *
 * The default values for the supported data types are:
 *  - number: 0
 *  - string: ''
 *  - boolean: false
 */
export function createDefaultForSchema<T>(schema: SchemaObject): T {
  if (schema['type'] !== 'object') {
    throw new Error('schema type must be "object"');
  }

  const properties = schema['properties'] as Record<string, SchemaProperty>;
  const required = schema['required'] as string[];
  const returnValue: Record<string, unknown> = {};

  for (const key of Object.keys(properties)) {
    if (required.includes(key)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      returnValue[key] = getDefaultValueForType(properties[key]!.type);
    }
  }

  return returnValue as T;
}

export type ValidatorError = ErrorObject<string, Record<string, unknown>>;

/**
 * Prepares AJV-based validator errors for logging
 *
 * @param validator AJV validator instance
 * @returns array of AJV validator errors
 */
export function prepareAjvErrors(
  validator: ValidateFunction,
): ValidatorError[] {
  return [
    ...(validator.errors && validator.errors.length > 0
      ? validator.errors
      : []),
  ];
}
